import {
  deleteReportSchedules,
  getReportSchedulesById,
  getReportSchedulesList,
  postReportSchedules,
  updateReportSchedules,
} from '@/apis/homeAPI';
import { GetUsersByTeamIds } from '@/apis/settings';
import { DownloadRawData, GetPreCalculatedReport } from '@/apis/shipment';
import { ApiResponse } from '@/apis/types';
import { CloseFilledIcon, CrossIcon, PencilIcon } from '@/assets/icons/icons';
import { useToken } from '@/common/helper';
import { BeaconEventType } from '@/common/enum';
import { CustomButton, SearchWithMatchNavigator } from '@/components';
import { useSpin } from '@/components/SpinContext.jsx';
import { trackEvent } from '@/hooks';
import useUnauthorize from '@/hooks/useUnauthorize';
import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { Col, Divider, Flex, Input, message, Modal, Row } from 'antd';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  DownloadReportFooter,
  DownloadReportModalHeader,
  DownloadReportTableSection,
  DownloadReportPannelSection,
  DownloadReportTabs,
  makeDefaultSheet,
  ReportRefreshButton,
} from './components';
import ScheduleDownloadModal, {
  Schedule,
} from './components/ScheduleDownloadModal';
import {
  FiltersPayload,
  ReportScheduleApi,
  ReportScheduleFormPayload,
  ShipmentReportData,
  TimeSetting,
  DownloadFormat,
  HeaderItem,
  ShipmentItem,
  TabItem,
  PermissionsObject,
} from './types';
import type { PresetInterface } from './components/PresetsDropdown';
import {
  applyCustomDraftsToRows,
  buildCustomRowKey,
  type CustomCellDraftStore,
  identityFromRow,
  materializeTabsForPresetSave,
  scrubCustomColumnFromDrafts,
  seedDraftsFromRows,
  setCustomCellDraft,
} from './utils/customCellDrafts';
import {
  AppliedFilter,
  FilterArrayItem,
  ShipmentFiltersPayload,
} from '@/pages/Shipments/ControlTower/types';
import ShipmentsFilterPanel from '@/pages/Shipments/ControlTower/components/ShipmentFiltersPanel';
import { resolvePeriodRange } from '@/pages/AiHistory/utils/resolvePeriodRange';
import { filtersList } from '@/common/static_data';
import {
  normalizeFilterLogic,
  supportsFilterLogic,
} from '@/pages/Shipments/Filters/filterLogic';
import { toLabel } from '@/pages/Shipments/Filters/utils/labels';
import { ReportRefreshButtonRef } from './components/ReportRefreshButton';

interface DownloadReportModalProps {
  reportModalOpen: boolean;
  setReportModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handleCancel?: () => void;
  onBack?: () => void;
  title: string;
  showBackIcon: boolean;
  permissions: PermissionsObject;
}
export interface teams {
  color: string;
  id: string;
  name: string;
}
const REPORT_PAGE_SIZE = 10;
const EMPTY_SHIPMENT_DATA: ShipmentItem[] = [];
type PreCalculatedCalculating = {
  status: 'CALCULATING';
  task_id: string;
  message?: string;
};
export interface FilterItemCompat {
  filterBy: string;
  filterValue: string[];
}
type PreCalculatedCompleted = ShipmentReportData & {
  page: number;
  page_size: number;
  total_pages: number;
  last_refresh_time: string | null;
  calc_window_start?: string | null;
  calc_window_end?: string | null;
  calculation_status: 'COMPLETED';
};

// Server wrapper type: { data: { data: ... } }
type ApiMain = {
  data: PreCalculatedCalculating | PreCalculatedCompleted;
  is_success: boolean;
  message?: string;
  status?: number;
};

const DownloadReportModal: React.FC<DownloadReportModalProps> = ({
  reportModalOpen,
  setReportModalOpen,
  onBack,
  title,
  showBackIcon,
  permissions,
}) => {
  const [multipleTabs, setMultipleTabs] = useState<TabItem[]>([
    makeDefaultSheet(0),
  ]);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const reportFilterStateRef = useRef<{
    filterArray: FilterArrayItem[];
    appliedFilters: AppliedFilter[];
    field_timezones?: Record<string, TimeSetting>;
    tabIndex?: number;
  }>({
    filterArray: [],
    appliedFilters: [],
    field_timezones: {},
    tabIndex: 0,
  });
  const isTabSwitchRef = useRef(false);
  const isAddingTabRef = useRef(true);
  const [canUpdateHeaders, setCanUpdateHeaders] = useState(true);
  const canUpdateHeadersRef = useRef(canUpdateHeaders);
  useEffect(() => {
    canUpdateHeadersRef.current = canUpdateHeaders;
  }, [canUpdateHeaders]);
  const [activeTabTimeFilterFetch, setActiveTabTimeFilterFetch] =
    useState(false);
  const loadingMoreRef = useRef(false);
  const customColumnCountersRef = useRef<Record<string, number>>({});
  // In-session Notes drafts that survive search/refetch (cleared when modal closes).
  const customCellDraftsRef = useRef<CustomCellDraftStore>({});
  const [scrollOnNextAdd, setScrollOnNextAdd] = useState(false);
  // tracks last search query each tab was fetched with; undefined = never fetched, '' = fetched with no search
  const tabLastSearchRef = useRef<Map<number, string>>(new Map());
  // skip a single API call triggered by activeTabIndex change when tab already has correct data
  const skipApiCallRef = useRef(false);
  const inFlightReportKeysRef = useRef<Set<string>>(new Set());
  const lastReportFetchRef = useRef<{ key: string; at: number } | null>(null);
  const fetchShipmentReportDataRef = useRef<
    | ((
        page?: number,
        resetData?: boolean,
        filterArray?: FilterArrayItem[],
        appliedFiltersArray?: AppliedFilter[],
        updateHeaders?: boolean,
        appendData?: boolean,
        tabIndex?: number,
        timeZoneFilter?: Record<string, TimeSetting>
      ) => void)
    | null
  >(null);
  const [presetHeaderAnchorEl, setPresetHeaderAnchorEl] =
    useState<HTMLSpanElement | null>(null);
  const [reportPanelSearchQuery, setReportPanelSearchQuery] = useState('');
  const [reportPanelSearchVisible, setReportPanelSearchVisible] =
    useState(false);
  const presetHeaderAnchorRef = useCallback((el: HTMLSpanElement | null) => {
    setPresetHeaderAnchorEl((prev) => (prev === el ? prev : el));
  }, []);

  //This function will run when api runs
  const handleActiveTab = (
    completed: PreCalculatedCompleted,
    updatedHeader: boolean,
    page: number,
    append: boolean,
    tabIndex: number,
    dataOverride?: ShipmentItem[]
  ) => {
    const shouldUpdateHeaders = isAddingTabRef.current;
    const nextData = dataOverride ?? completed?.data ?? [];
    setMultipleTabs((prevTabs) =>
      prevTabs.map(
        (tab, index) =>
          index === tabIndex
            ? {
                ...tab, // keep all other properties the same
                headers:
                  shouldUpdateHeaders === true ? completed.header : tab.headers,
                data: append ? [...(tab.data ?? []), ...nextData] : nextData,
                hasMore: page < (completed.total_pages || 1),
                total_rows: completed.total,
                page: page,
              }
            : tab // keep other tabs unchanged
      )
    );
    if (shouldUpdateHeaders) {
      isAddingTabRef.current = false;
    }
  };

  const TableScrollToTop = () => {
    const tableBody = document.querySelector('.ant-table-body');
    if (tableBody) {
      tableBody.scrollTop = 0;
    }
  };

  const handleTabChange = (ind: number) => {
    setActiveTabIndex(ind);
    setCanUpdateHeaders(false);
    TableScrollToTop();
    const allTabs = [...multipleTabs];
    const tab = allTabs[ind];

    // CASE 1: Search is active — only force API if tab hasn't been fetched with this exact query yet
    if (debouncedSearchQuery) {
      const lastQuery = tabLastSearchRef.current.get(ind);
      if (lastQuery === debouncedSearchQuery) {
        // tab already has correct search results, no need to refetch
        skipApiCallRef.current = true;
        return;
      }
      // different query or never searched — force API with page 1
      setMultipleTabs((prev) =>
        prev.map((t, i) => (i === ind ? { ...t, page: 1 } : t))
      );
      isTabSwitchRef.current = false;
      return;
    }

    // CASE 2: Search was closed but this tab still has stale search data → reset it
    const lastQuery = tabLastSearchRef.current.get(ind);
    if (lastQuery !== undefined && lastQuery !== '') {
      setMultipleTabs((prev) =>
        prev.map((t, i) => (i === ind ? { ...t, page: 1 } : t))
      );
      isTabSwitchRef.current = false;
      return;
    }

    // CASE 3: existing logic — preset applied and tab has no data → fetch
    if (presetId && (!Array.isArray(tab?.data) || tab.data.length === 0)) {
      isTabSwitchRef.current = false;
    } else {
      isTabSwitchRef.current = true;
    }
  };

  const handleJointFilters = useCallback(
    (filterArr: FilterArrayItem[], appliedArr: AppliedFilter[]) => {
      const tabIndex = activeTabIndex;
      reportFilterStateRef.current = {
        filterArray: [...filterArr],
        appliedFilters: [...appliedArr],
      };
      skipApiCallRef.current = true;
      setMultipleTabs((prevTabs) => {
        const updatedTabs = prevTabs.map((tab, index) => {
          if (index !== activeTabIndex) return tab;
          return {
            ...tab,
            filters: {
              ...tab.filters,
              appliedFilters: [...appliedArr],
              filterArray: [...filterArr],
            },
            page: 1,
          };
        });

        const timezoneFilter =
          prevTabs[activeTabIndex]?.filters?.field_timezones ?? {};

        fetchShipmentReportDataRef.current?.(
          1,
          true,
          filterArr,
          appliedArr,
          false,
          false,
          tabIndex,
          timezoneFilter
        );

        return updatedTabs;
      });
      TableScrollToTop();
    },
    [activeTabIndex]
  );

  useEffect(() => {
    const tab = multipleTabs[activeTabIndex];
    reportFilterStateRef.current = {
      filterArray: [...(tab?.filters?.filterArray ?? [])],
      appliedFilters: [...(tab?.filters?.appliedFilters ?? [])],
      field_timezones: tab?.filters?.field_timezones ?? {},
      tabIndex: activeTabIndex,
    };
  }, [activeTabIndex, multipleTabs]);

  const updateActiveReportFilters = useCallback(
    (
      updater: (current: {
        filterArray: FilterArrayItem[];
        appliedFilters: AppliedFilter[];
      }) => {
        filterArray: FilterArrayItem[];
        appliedFilters: AppliedFilter[];
      }
    ) => {
      const current = {
        filterArray: reportFilterStateRef.current.filterArray ?? [],
        appliedFilters: reportFilterStateRef.current.appliedFilters ?? [],
      };
      const next = updater(current);

      reportFilterStateRef.current = {
        filterArray: [...next.filterArray],
        appliedFilters: [...next.appliedFilters],
      };

      setMultipleTabs((prevTabs) =>
        prevTabs.map((tab, index) => {
          if (index !== activeTabIndex) return tab;

          return {
            ...tab,
            filters: {
              ...tab.filters,
              filterArray: [...next.filterArray],
              appliedFilters: [...next.appliedFilters],
            },
            page: 1,
          };
        })
      );
    },
    [activeTabIndex]
  );

  const setReportAppliedFilters = useCallback(
    (nextApplied: React.SetStateAction<AppliedFilter[]>) => {
      updateActiveReportFilters((current) => {
        const appliedFilters =
          typeof nextApplied === 'function'
            ? nextApplied(current.appliedFilters)
            : nextApplied;

        return {
          ...current,
          appliedFilters: [...appliedFilters],
        };
      });
    },
    [updateActiveReportFilters]
  );

  const setReportFilterArray = useCallback(
    (nextFilterArray: FilterArrayItem[]) => {
      updateActiveReportFilters((current) => ({
        ...current,
        filterArray: [...nextFilterArray],
      }));
    },
    [updateActiveReportFilters]
  );

  const handleReportFilterRunSearch = useCallback(
    (_params: {
      query: string;
      check: boolean;
      page?: number;
      pageSize?: number;
      calledFrom?: string;
      payload?: ShipmentFiltersPayload | null;
    }) => {
      // Ignore the automatic re-run triggered by ShipmentsFilterPanel when
      // appliedFilters/filterArray props change due to a tab add or switch.

      const { filterArray, appliedFilters } = reportFilterStateRef.current;

      handleJointFilters(filterArray, appliedFilters);
    },
    [handleJointFilters]
  );

  const handleupdateHeaders = (updatedHeaderList: HeaderItem[]) => {
    setMultipleTabs((prevTabs) =>
      prevTabs.map((tab, index) =>
        index === activeTabIndex
          ? {
              ...tab,
              headers: [...updatedHeaderList],
            }
          : tab
      )
    );
  };

  const handleDeleteCustomColumn = (columnKey: string) => {
    scrubCustomColumnFromDrafts(
      customCellDraftsRef.current,
      activeTabIndex,
      columnKey
    );
    setHeaderList((prev) => prev.filter((col) => col.key !== columnKey));
    setMultipleTabs((prev) =>
      prev.map((tab, index) =>
        index === activeTabIndex
          ? {
              ...tab,
              headers:
                tab.headers?.filter((col) => col.key !== columnKey) || [],
              data:
                tab.data?.map((shipment) => {
                  const updated = { ...shipment };
                  delete updated[columnKey];
                  return updated;
                }) || [],
            }
          : tab
      )
    );
  };

  const [headerList, setHeaderList] = useState<HeaderItem[]>([]);
  const [shipmentData, setShipmentData] = useState<ShipmentItem[]>([]);
  const [sheetTitle, setSheetTitle] = useState('Spreadsheet 001');
  const headerListRef = useRef<HeaderItem[]>([]);
  const customColumnsRef = useRef<HeaderItem[]>([]);
  const shipmentDataRef = useRef<ShipmentItem[]>([]);
  // Tab headers own custom Notes columns; keep a ref so fetch can read them
  // without putting multipleTabs in the callback deps.
  const multipleTabsRef = useRef(multipleTabs);
  const { checkTokenExpiry, decodedUserToken, token } = useToken();
  const user = decodedUserToken();
  const { unauthorize } = useUnauthorize();
  const { showSpin, hideSpin } = useSpin();
  const [presetId, setPresetId] = useState('');
  const [lastRefreshTime, setLastRefreshTime] = useState<string | null>(null);
  const loggedInUserId = decodedUserToken()?.email;
  const refreshButtonRef = useRef<ReportRefreshButtonRef>(null);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<Schedule | null>(null);
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [recipient, setRecipient] = useState<unknown>(null);

  const getUsersByTeams = useCallback(() => {
    GetUsersByTeamIds(token ?? '', [])
      .then((res: ApiResponse) => {
        const data = res?.data?.data;
        if (data.is_success) {
          setRecipient(data?.data);
        } else {
          setRecipient([]);
        }
      })
      .catch((error) => {
        unauthorize(error);
      });
  }, [token, unauthorize]);

  useEffect(() => {
    getUsersByTeams();
  }, [unauthorize, getUsersByTeams]);
  const handleCreateSchedule = () => {
    setEditingSchedule(null);
    setScheduleModalOpen(true);
  };
  const handleDeleteSchedule = (id?: string | number | null) => {
    if (!id) return;

    const token = checkTokenExpiry() ?? '';
    showSpin();

    deleteReportSchedules(token, String(id))
      .then((res) => {
        const data = res?.data || null;
        if (data?.data?.is_success) {
          message.success(data?.data?.message || 'Schedule deleted');
          setScheduleModalOpen(false);
          loadSchedules();
        } else {
          message.error(data?.data?.message || 'Failed to delete schedule');
        }
      })
      .catch((error) => {
        console.error('Error deleting schedule:', error?.response || error);
        message.error('Failed to delete schedule');
        unauthorize(error);
      })
      .finally(() => {
        hideSpin();
      });
  };
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

  useEffect(() => {
    const handler = window.setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 2000);

    return () => {
      window.clearTimeout(handler);
    };
  }, [searchQuery]);

  //-----------------Report Schedule----------------//
  const handleEditSchedule = (id: Schedule['id']) => {
    if (!id) return;

    const token = checkTokenExpiry() ?? '';

    getReportSchedulesById(token, String(id))
      .then((res) => {
        const apiSchedule = res.data.data.data?.schedule as ReportScheduleApi;
        const labelToCode: Record<string, string> = {
          Mon: 'MON',
          Tue: 'TUE',
          Wed: 'WED',
          Thu: 'THU',
          Fri: 'FRI',
          Sat: 'SAT',
          Sun: 'SUN',
        };

        const repeat_days =
          apiSchedule.weekly_on_days?.map((label: string) => {
            const trimmed = label.trim();
            return labelToCode[trimmed] || trimmed.toUpperCase();
          }) ?? [];

        let monthlyDay: number | null = null;
        if (apiSchedule.monthly_on_day) {
          const match = String(apiSchedule.monthly_on_day).match(/\d+/);
          monthlyDay = match ? Number(match[0]) : null;
        }

        const editing: Schedule = {
          id: apiSchedule.id,
          name: apiSchedule.name,
          description: apiSchedule.description ?? '',
          repeat_every: apiSchedule.repeat_every,
          repeat_unit: apiSchedule.repeat_unit.toUpperCase() as
            'DAYS' | 'WEEKS' | 'MONTHS',
          frequency: apiSchedule.frequency,

          repeat_days,
          time_1: apiSchedule.time_1 ?? null,
          time_2: apiSchedule.time_2 ?? null,
          time_3: apiSchedule.time_3 ?? null,
          monthly_day: monthlyDay,
          monthly_time:
            apiSchedule.repeat_unit === 'months'
              ? (apiSchedule.time_1 ?? null)
              : null,
          notification_method: apiSchedule.notification_method ?? null,
          send_to: apiSchedule.send_to ?? [],
        };

        setEditingSchedule(editing);
        setScheduleModalOpen(true);
      })
      .catch((error) => {
        message.error('Failed to load schedule', error);
      });
  };

  const formatScheduleDescription = (item: ReportScheduleApi): string => {
    const times = [item.time_1, item.time_2, item.time_3].filter(Boolean);
    const timesStr = times.length ? ` @ ${times.join(', ')}` : '';

    // MONTHLY
    if (item.repeat_unit === 'months') {
      let dayNum: string | null = null;

      if (item.monthly_on_day) {
        const match = String(item.monthly_on_day).match(/\d+/);
        dayNum = match ? match[0] : null;
      }

      if (dayNum) {
        return `Monthly on day ${dayNum}${timesStr}`;
      }
      return `Monthly${timesStr}`;
    }

    // WEEKLY
    if (item.repeat_unit === 'weeks') {
      const shortMap: Record<string, string> = {
        Mon: 'Mo',
        Tue: 'Tu',
        Wed: 'We',
        Thu: 'Th',
        Fri: 'Fr',
        Sat: 'Sa',
        Sun: 'Su',
      };

      const days = (item.weekly_on_days || []).map((label: string) => {
        const trimmed = label.trim();
        return shortMap[trimmed] || trimmed.slice(0, 2);
      });

      if (days.length) {
        return `Weekly on ${days.join(', ')}${timesStr}`;
      }
      return `Weekly${timesStr}`;
    }

    // DAILY / every N days (default)
    if (item.repeat_unit === 'days') {
      switch (item.frequency) {
        case 'ONCE_A_DAY':
          return `Daily${item.time_1 ? ` @ ${item.time_1}` : ''}`;
        case 'TWICE_A_DAY':
          return times.length ? `Twice a day${timesStr}` : 'Twice a day';
        case 'THREE_TIMES_A_DAY':
          return times.length ? `3x daily${timesStr}` : '3x daily';
        default:
          if (item.repeat_every && item.repeat_every > 1) {
            return `Every ${item.repeat_every} days${timesStr}`;
          }
          return `Daily${timesStr}`;
      }
    }

    // Fallback
    return `Every ${item.repeat_every || 1} ${item.repeat_unit || 'days'}${timesStr}`;
  };

  const loadSchedules = useCallback(() => {
    const token = checkTokenExpiry() ?? '';
    getReportSchedulesList(token)
      .then((res) => {
        const apiSchedules: ReportScheduleApi[] =
          res?.data?.data?.data?.data || [];
        const mapped: Schedule[] = apiSchedules.map((item) => ({
          id: item.id,
          name: item.name,
          description: formatScheduleDescription(item),
        }));

        setSchedules(mapped);
      })
      .catch((err) => {
        unauthorize(err);
        setSchedules([]);
      })
      .finally(() => {});
  }, [checkTokenExpiry, unauthorize]);

  useEffect(() => {
    if (reportModalOpen) {
      loadSchedules();
    }
  }, [reportModalOpen, loadSchedules]);

  const handleScheduleSubmit = (payload: ReportScheduleFormPayload) => {
    //const filters = filtersState.payload ?? {};
    const token = checkTokenExpiry() ?? '';
    const isEdit = !!payload.id;
    const commonPayload = {
      ...payload,
      ...(presetId ? { preset: presetId } : {}),
      tab_presets: materializeTabsForPresetSave(
        multipleTabs,
        customCellDraftsRef.current
      ),
    };
    const createPayload = {
      ...commonPayload,
      name: sheetTitle,
      preset: presetId ? presetId : null,
    };
    showSpin();

    const request = isEdit
      ? updateReportSchedules(token, commonPayload, String(payload.id))
      : postReportSchedules(token, createPayload);

    request
      .then((res) => {
        const data = res?.data || null;
        if (data?.data?.is_success) {
          message.success(data?.data?.message || 'Schedule saved');
          setScheduleModalOpen(false);
          setEditingSchedule(null);
          loadSchedules();
        } else {
          message.error(data?.data?.message || 'Failed to save settings');
        }
      })
      .catch((error) => {
        console.error('Error saving schedule:', error?.response || error);
        message.error('Failed to save settings');
      })
      .finally(() => {
        hideSpin();
      });
  };

  useEffect(() => {
    headerListRef.current = headerList;
  }, [headerList]);

  useEffect(() => {
    multipleTabsRef.current = multipleTabs;
    // Notes columns live on tab.headers (handleAddCustomColumn does not
    // always sync headerList), so derive custom columns from the active tab.
    customColumnsRef.current = (
      multipleTabs[activeTabIndex]?.headers || []
    ).filter((item) => item.isCustom);
  }, [multipleTabs, activeTabIndex]);

  useEffect(() => {
    shipmentDataRef.current = shipmentData;
  }, [shipmentData]);

  const handleAddCustomColumn = () => {
    // Initialize counter for current tab if missing
    if (!customColumnCountersRef.current[activeTabIndex]) {
      customColumnCountersRef.current[activeTabIndex] = 0;
    }

    // Increment counter for current tab only
    const nextCount = customColumnCountersRef.current[activeTabIndex] + 1;
    customColumnCountersRef.current[activeTabIndex] = nextCount;

    const columnLabel = nextCount === 1 ? 'Notes' : `Notes ${nextCount}`;
    const columnKey = `custom_${Date.now()}_${nextCount}`;

    const newColumn: HeaderItem = {
      key: columnKey,
      value: columnLabel,
      enable: true,
      isCustom: true,
    };

    setHeaderList((prev) => [...prev, newColumn]);

    setMultipleTabs((prev) =>
      prev.map((tab, index) =>
        index === activeTabIndex
          ? {
              ...tab,
              headers: [...(tab.headers || []), newColumn],
              data:
                tab.data?.map((row) => ({
                  ...row,
                  [columnKey]: '',
                })) || [],
            }
          : tab
      )
    );
  };
  const handleCustomCellChange = useCallback(
    (rowIndex: number, columnKey: string, value: string) => {
      setMultipleTabs((prev) =>
        prev.map((tab, ind) => {
          if (ind !== activeTabIndex) return tab;
          const row = tab.data?.[rowIndex];
          const rowKey = buildCustomRowKey(row);
          if (rowKey) {
            setCustomCellDraft(
              customCellDraftsRef.current,
              activeTabIndex,
              rowKey,
              columnKey,
              value,
              identityFromRow(row)
            );
          }
          return {
            ...tab,
            data:
              tab.data?.map((r, index) =>
                index === rowIndex ? { ...r, [columnKey]: value } : r
              ) || [],
          };
        })
      );
      // Keep shipmentData in sync so any path reading it also sees the edit.
      setShipmentData((prev) =>
        prev.map((r, index) =>
          index === rowIndex ? { ...r, [columnKey]: value } : r
        )
      );
    },
    [activeTabIndex]
  );

  const handleHeaderUpdate = useCallback(
    (oldKey: string, _newKey: string, newValue: string) => {
      setMultipleTabs((prev) =>
        prev.map((tab, index) =>
          index === activeTabIndex
            ? {
                ...tab,
                headers: (tab.headers ?? []).map((h) =>
                  h.key === oldKey ? { ...h, value: newValue } : h
                ),
              }
            : tab
        )
      );
    },
    [activeTabIndex]
  );

  //used for tabs  time zone filer
  const onTimeSettingsChange = useCallback(
    (columnKey: string, patch: Partial<TimeSetting>) => {
      setCanUpdateHeaders(false);
      TableScrollToTop();
      isTabSwitchRef.current = false;
      setMultipleTabs((prev) =>
        prev.map((tab, index) => {
          if (index !== activeTabIndex) return tab;
          const prevField = tab.filters?.field_timezones?.[columnKey];
          return {
            ...tab,
            filters: {
              ...(tab.filters ?? {}), // keep existing filters
              field_timezones: {
                ...(tab.filters?.field_timezones ?? {}), // keep old timezones
                [columnKey]: {
                  timezone:
                    patch.timezone ?? prevField?.timezone ?? 'local_scan_time',
                  format: patch.format ?? prevField?.format ?? '%Y-%d-%m %H:%M',
                },
              },
            },
            page: 1,
          };
        })
      );

      setActiveTabTimeFilterFetch((prev) => !prev);
    },
    [activeTabIndex]
  );

  const downloadPdfReport = (
    headers: HeaderItem[],
    rows: Record<string, unknown>[],
    filename: string
  ) => {
    const doc = new jsPDF({ orientation: 'landscape' });
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.text(sheetTitle, 14, 18);

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const filterLines: string[] = [];
    const filterArray =
      multipleTabs[activeTabIndex]?.filters?.filterArray ?? [];
    if (filterArray.length > 0) {
      filterArray.forEach((element) => {
        const name =
          filtersList.find((x) => x.key === element.filterBy)?.label ||
          toLabel(element.filterBy);
        if (
          Array.isArray(element?.filterValue) &&
          element?.filterValue?.length > 0
        ) {
          filterLines.push(`${name}: ${element.filterValue.join(', ')}`);
        }
      });
    }
    let y = 26;
    filterLines.forEach((line) => {
      const wrapped = doc.splitTextToSize(line, 270);
      doc.text(wrapped, 14, y);
      y += wrapped.length * 6;
    });

    const tableHeaders = headers.map(
      (header) => header.value ?? header.key ?? ''
    );
    const tableRows = rows.map((row) =>
      tableHeaders.map((_title, index) => {
        const key = headers[index]?.key;
        const cellValue = key ? row[key] : '';
        if (cellValue == null) {
          return '';
        }
        if (typeof cellValue === 'object') {
          try {
            return JSON.stringify(cellValue);
          } catch {
            return '';
          }
        }
        return String(cellValue);
      })
    );

    autoTable(doc, {
      head: [tableHeaders],
      body: tableRows,
      startY: Math.max(y, 30),
      styles: {
        fontSize: 8,
        cellPadding: 3,
        cellWidth: 'wrap',
      },
      headStyles: {
        fillColor: [0, 0, 0],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245],
      },
    });

    doc.save(filename);
  };

  const downloadReport = (blob: Blob, filename = 'shipment-report.xlsx') => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };
  const [loading, setLoading] = useState(true);

  const DATE_KEYS = useMemo(
    () =>
      new Set([
        'scheduled_delivery_date',
        'scheduled_pickup_date',
        'customer_defined_ship_date',
        'created_at',
      ]),
    []
  );

  const buildPayload = useCallback(
    (
      data: FilterArrayItem[] | FilterItemCompat[] | null | undefined,
      applied: AppliedFilter[] = []
    ): FiltersPayload | null => {
      if (!data || data.length === 0) return null;

      const out: FiltersPayload = {};

      for (const item of data) {
        const k = item.filterBy;
        if (!k) continue;

        const values = Array.isArray(item.filterValue)
          ? item.filterValue.map(String)
          : [String(item.filterValue)];

        if (DATE_KEYS.has(k)) continue;

        if (k === 'type') {
          const nums = values
            .map((v) => Number(v))
            .filter((v) => !Number.isNaN(v));
          if (!nums.length) continue;
          if (!Array.isArray(out.type)) {
            out.type = [];
          }
          out.type = Array.from(new Set([...out.type, ...nums]));
          continue;
        }

        const existing = out[k];
        const next = new Set<string>(
          Array.isArray(existing)
            ? existing.map(String)
            : existing &&
                typeof existing === 'object' &&
                'values' in existing &&
                Array.isArray((existing as { values?: unknown }).values)
              ? (existing as { values: unknown[] }).values.map(String)
              : existing
                ? [String(existing)]
                : []
        );
        values.forEach((v) => next.add(v));

        const nextValues = Array.from(next);
        out[k] = supportsFilterLogic(k)
          ? {
              values: nextValues,
              logic: normalizeFilterLogic((item as { logic?: unknown }).logic),
            }
          : nextValues;
      }

      for (const item of data) {
        const k = item.filterBy;
        if (!DATE_KEYS.has(k)) continue;

        const ids = Array.isArray(item.filterValue)
          ? item.filterValue.map(String)
          : [String(item.filterValue)];

        const matches = applied.filter(
          (af) =>
            (af.dateFilter === k || af.filterBy === k) &&
            ids.includes(String(af.id))
        );

        if (!matches.length) continue;

        const bucket: Record<
          number,
          { period?: string; start?: string; end?: string }
        > = {};

        matches.forEach((af, idx) => {
          let parsedId: {
            period?: string;
            start?: string;
            end?: string;
          } | null = null;

          try {
            parsedId = JSON.parse(String(af.id));
          } catch {
            parsedId = null;
          }

          if (parsedId?.period) {
            const range = resolvePeriodRange(
              parsedId.period,
              user?.user_time_zone
            );
            if (range) {
              bucket[idx] = {
                period: parsedId.period,
                start: range.start,
                end: range.end,
              };
              return;
            }
          }

          if (af.start || af.end) {
            bucket[idx] = {
              start: af.start,
              end: af.end,
              period: parsedId?.period,
            };
          } else if (parsedId?.start && parsedId?.end) {
            bucket[idx] = { start: parsedId.start, end: parsedId.end };
          }
        });

        if (Object.keys(bucket).length) {
          out[k] = bucket;
        }
      }

      return Object.keys(out).length ? out : null;
    },
    [DATE_KEYS, user?.user_time_zone]
  );

  const skipNextFilterUpdate = useRef(false);
  const pendingPresetApplyIdRef = useRef<string | null>(null);

  const handlePresetApplied = useCallback((preset: PresetInterface) => {
    skipNextFilterUpdate.current = true;
    const appliedPresetId = preset.id ?? '';
    pendingPresetApplyIdRef.current = appliedPresetId;
    skipApiCallRef.current = false;
    isTabSwitchRef.current = false;
    tabLastSearchRef.current.clear();
    // Fresh drafts from whatever custom values are stored on the preset rows.
    customCellDraftsRef.current = {};
    const nextTabs =
      preset?.tab_presets?.map((tab) => ({
        ...tab,
        page: 1,
      })) || [];
    nextTabs.forEach((tab, index) => {
      const customKeys = (tab.headers || [])
        .filter((h) => h.isCustom && h.key)
        .map((h) => String(h.key));
      seedDraftsFromRows(
        customCellDraftsRef.current,
        index,
        tab.data,
        customKeys
      );
    });
    setMultipleTabs(() => nextTabs);
    setCanUpdateHeaders(false);
    setActiveTabIndex(0);
    setPresetId(appliedPresetId);
    setSheetTitle((current) => preset.name?.trim() || current);
    // Track viewing this report preset. Its absent shipment_id is what
    // tells the backend to resolve reference_id against ReportPreset
    // instead of TemperatureGxpReport.
    trackEvent(BeaconEventType.ReportViewed, {
      reference_id: appliedPresetId,
    });
  }, []);

  const getTabsForSave = useCallback(
    () =>
      materializeTabsForPresetSave(
        multipleTabsRef.current,
        customCellDraftsRef.current
      ),
    []
  );

  useEffect(() => {
    headerListRef.current = headerList;
  }, [headerList]);

  const fetchShipmentReportData = useCallback(
    (
      pageArg?: number,
      resetData = true,
      filterArrayArg?: FilterArrayItem[],
      appliedFiltersArrayArg?: AppliedFilter[],
      updateHeadersArg?: boolean,
      appendData = false,
      tabIndex = activeTabIndex,
      fieldTimezoneArg?: Record<string, TimeSetting>
    ) => {
      const activeTab = multipleTabsRef.current[activeTabIndex];
      const page = pageArg ?? activeTab?.page ?? 1;
      const filterArray =
        filterArrayArg ?? activeTab?.filters?.filterArray ?? [];
      const appliedFiltersArray =
        appliedFiltersArrayArg ?? activeTab?.filters?.appliedFilters ?? [];
      const updateHeaders = updateHeadersArg ?? canUpdateHeadersRef.current;
      const fieldTimezone =
        fieldTimezoneArg ?? activeTab?.filters?.field_timezones;
      const token = checkTokenExpiry() ?? '';
      const payload = buildPayload(filterArray, appliedFiltersArray) || null;

      const body: FiltersPayload & {
        page: number;
        page_size: number;
        preset_id: string;
      } = {
        ...(payload ?? {}),
        ...(fieldTimezone && Object.keys(fieldTimezone)?.length > 0
          ? { field_timezones: fieldTimezone }
          : {}),
        page: page,
        page_size: REPORT_PAGE_SIZE,
        preset_id: presetId,
        search: debouncedSearchQuery,
      };

      const requestKey = JSON.stringify({
        body,
        tabIndex,
      });
      const lastFetch = lastReportFetchRef.current;
      const isRecentDuplicate =
        lastFetch?.key === requestKey && Date.now() - lastFetch.at < 500;

      if (inFlightReportKeysRef.current.has(requestKey) || isRecentDuplicate) {
        loadingMoreRef.current = false;
        return;
      }

      inFlightReportKeysRef.current.add(requestKey);
      lastReportFetchRef.current = { key: requestKey, at: Date.now() };
      setLoading(true);

      GetPreCalculatedReport(token, body)
        .then((res: { data?: { data?: ApiMain } }) => {
          //setFilterUpdate(false)
          // API shape: { data: { data: { data: CompletedPayload | CalculatingPayload, ...meta } } }
          const main = res?.data?.data as ApiMain | undefined;
          const payload = main?.data;
          const isCalculating = (
            p: ApiMain['data']
          ): p is PreCalculatedCalculating => {
            return (
              !!p &&
              typeof p === 'object' &&
              'status' in p &&
              (p as PreCalculatedCalculating).status === 'CALCULATING'
            );
          };

          const isCompleted = (
            p: ApiMain['data']
          ): p is PreCalculatedCompleted => {
            return !!p && typeof p === 'object' && 'data' in p && 'header' in p;
          };

          if (!payload) {
            setHeaderList((prev) => prev.filter((item) => item.isCustom));
            setShipmentData([]);
            return;
          }

          // If backend reports calculation in progress
          if (isCalculating(payload)) {
            refreshButtonRef.current?.triggerPolling(payload.task_id);
            return;
          }

          if (!isCompleted(payload)) {
            setHeaderList((prev) => prev.filter((item) => item.isCustom));
            setShipmentData([]);
            return;
          }

          const completed = payload;
          const shipments = (completed.data ?? []) as ShipmentItem[];
          const headersFromApi = (completed.header ?? []) as HeaderItem[];

          // Prefer the tab that owns this fetch — custom Notes columns live on
          // tab.headers, not always on headerList / customColumnsRef.
          const tabCustomKeys = (
            multipleTabsRef.current[tabIndex]?.headers ?? []
          )
            .filter((h) => h.isCustom && h.key)
            .map((h) => String(h.key));
          const customKeys = Array.from(
            new Set([
              ...tabCustomKeys,
              ...customColumnsRef.current
                .map((col) => col.key)
                .filter((key): key is string => !!key),
            ])
          );
          // Capture Notes from live tab rows (where cell edits land) and from
          // shipmentData as a fallback before the result set is replaced.
          seedDraftsFromRows(
            customCellDraftsRef.current,
            tabIndex,
            multipleTabsRef.current[tabIndex]?.data,
            customKeys
          );
          seedDraftsFromRows(
            customCellDraftsRef.current,
            tabIndex,
            shipmentDataRef.current,
            customKeys
          );

          const enrichedShipments = applyCustomDraftsToRows(
            shipments,
            customKeys,
            customCellDraftsRef.current,
            tabIndex
          );

          // If header includes a generic 'name' but records expose 'product_name',
          // copy product_name into name for display (avoid in-place mutation).
          const hasGenericName = headersFromApi.some((h) => h.key === 'name');
          const finalShipments: ShipmentItem[] = (() => {
            if (
              hasGenericName &&
              enrichedShipments.length &&
              !('name' in (enrichedShipments[0] as Record<string, unknown>))
            ) {
              const first = enrichedShipments[0] as Record<string, unknown>;
              if ('product_name' in first) {
                return enrichedShipments.map((row) => {
                  const r: Record<string, unknown> = row;
                  if (typeof r.product_name === 'string') {
                    return {
                      ...(r as ShipmentItem),
                      name: r.product_name as string,
                    };
                  }
                  return row;
                });
              }
            }
            return enrichedShipments;
          })();

          handleActiveTab(
            completed,
            updateHeaders,
            page,
            appendData,
            tabIndex,
            finalShipments
          );
          tabLastSearchRef.current.set(tabIndex, debouncedSearchQuery ?? '');

          setLastRefreshTime(completed.last_refresh_time || null);

          // Reset or append data based on page
          if (resetData || page === 1) {
            setShipmentData(finalShipments);
          } else {
            setShipmentData((prev) => [...prev, ...finalShipments]);
          }
          const mergedHeaders = (() => {
            // 1) Normalize incoming headers
            const currentHeaderList = headerListRef.current;
            let incomingHeaders = headersFromApi.map((item: HeaderItem) => ({
              ...item,
              enable: item.enable ?? true,
            }));

            // 2) Fallback if API returned nothing
            if (!incomingHeaders.length && shipments.length) {
              const sample = shipments[0] as Record<string, unknown>;
              incomingHeaders = Object.keys(sample).map((key) => ({
                key,
                value: key
                  .replace(/_/g, ' ')
                  .replace(/\b\w/g, (c) => c.toUpperCase()),
                enable: true,
              }));
            }

            // 3) Merge with previous headers
            if (!currentHeaderList.length) return incomingHeaders;

            const incomingMap = new Map(
              incomingHeaders.map((item) => [item.key, item])
            );
            const merged: HeaderItem[] = [];

            currentHeaderList.forEach((existing) => {
              if (existing.isCustom) {
                merged.push(existing);
                return;
              }

              const updated = incomingMap.get(existing.key);
              if (updated) {
                merged.push({
                  ...updated,
                  enable: existing.enable ?? updated.enable ?? true,
                });
                incomingMap.delete(existing.key);
              }
            });

            incomingMap.forEach((item) =>
              merged.push({ ...item, enable: item.enable ?? true })
            );

            return merged;
          })();

          // setApiHeaders(mergedHeaders);
          setHeaderList(mergedHeaders);

          //     setReportTabs((prev) =>
          // prev.map((tab) =>
          //   tab.id === activeTab ? { ...tab, headers: mergedHeaders } : tab
          // ))
        })
        .catch((err) => {
          unauthorize(err);
          setHeaderList((prev) => prev.filter((item) => item.isCustom));
          setShipmentData([]);
        })
        .finally(() => {
          inFlightReportKeysRef.current.delete(requestKey);
          setLoading(false);
          loadingMoreRef.current = false;
        });
    },
    [
      checkTokenExpiry,
      //multipleTabs.length,
      activeTabIndex,
      presetId,
      debouncedSearchQuery,
      unauthorize,
      buildPayload,
    ]
  );

  useEffect(() => {
    if (skipApiCallRef.current) {
      skipApiCallRef.current = false;
      return;
    }
    const pendingPresetApplyId = pendingPresetApplyIdRef.current;
    if (pendingPresetApplyId) {
      if (presetId !== pendingPresetApplyId) return;
      pendingPresetApplyIdRef.current = null;
    }
    if (isTabSwitchRef.current && !debouncedSearchQuery) return;
    fetchShipmentReportData();
    isTabSwitchRef.current = false;
  }, [
    fetchShipmentReportData,
    debouncedSearchQuery,
    presetId,
    activeTabTimeFilterFetch,
  ]);

  // Keep a ref to the latest fetch function to avoid hook cycles
  useEffect(() => {
    fetchShipmentReportDataRef.current = fetchShipmentReportData;
  }, [fetchShipmentReportData]);

  const handleDownload = (format: DownloadFormat = 'excel') => {
    const safeTitle = (sheetTitle || 'shipment-report').replace(
      /[\\/:*?"<>|]/g,
      '_'
    );

    // PDF is still generated client-side from rows loaded in the preview.
    if (format === 'pdf') {
      const updatedHeader = (multipleTabs[activeTabIndex]?.headers ?? [])
        .filter((x) => x?.enable)
        .map(({ key, value, enable }) => ({ key, value, enable }));
      const allowedKeys = updatedHeader.flatMap((header) =>
        typeof header.key === 'string' ? [header.key] : []
      );
      if (!allowedKeys.length) {
        message.warning('No enabled headers available to download');
        return;
      }

      const customKeys = (multipleTabs[activeTabIndex]?.headers ?? [])
        .filter((h) => h.isCustom && h.key)
        .map((h) => String(h.key));
      // Prefer live tab rows (where edits land); merge drafts for safety.
      const sourceRows = applyCustomDraftsToRows(
        (multipleTabs[activeTabIndex]?.data ?? shipmentData) as ShipmentItem[],
        customKeys,
        customCellDraftsRef.current,
        activeTabIndex
      );

      const shipmentDataUpdate = sourceRows.map((element) => {
        const filteredObj: Record<string, unknown> = {};
        allowedKeys.forEach((key) => {
          if (key === 'teams' && Array.isArray(element[key])) {
            filteredObj[key] = (element[key] as teams[])
              .map((t) => t.name)
              .join(', ');
          } else if (
            key === 'latest_location' &&
            element[key] &&
            typeof element[key] === 'object'
          ) {
            const location = element[key] as {
              latest_lat?: number;
              latest_long?: number;
            };
            if (location?.latest_lat && location?.latest_long) {
              filteredObj[key] =
                `Lat: ${location.latest_lat.toFixed(6)}, Long: ${location.latest_long.toFixed(6)}`;
            } else {
              filteredObj[key] = '';
            }
          } else if (Object.prototype.hasOwnProperty.call(element, key)) {
            filteredObj[key] = element[key];
          }
        });
        return filteredObj;
      });

      if (!shipmentDataUpdate.length) {
        message.warning('No data available to download');
        return;
      }

      downloadPdfReport(updatedHeader, shipmentDataUpdate, `${safeTitle}.pdf`);
      message.success('PDF report downloaded successfully');
      return;
    }

    // CSV and Excel: full export for current filters (server-side, same as preview totals).
    const serverFileType = format === 'csv' ? 'csv' : 'excel';
    const reportTabsModels = multipleTabs?.map((tab) => {
      const tabPayload =
        buildPayload(tab.filters?.filterArray, tab.filters?.appliedFilters) ||
        {};

      const fieldTimezones = tab?.filters?.field_timezones;

      return {
        key: tab.sheet_name || `tab_${tab.id}`,
        title: sheetTitle,
        header: tab.headers,
        preset_id: presetId,
        file_type: serverFileType,
        sheet_name: tab.sheet_name,
        ...tabPayload,
        ...(fieldTimezones ? { field_timezones: fieldTimezones } : {}),
      };
    });

    const token = checkTokenExpiry() ?? '';
    showSpin();
    DownloadRawData(token, reportTabsModels)
      .then(async (res: { data: Blob }) => {
        const blob = res.data;

        if (blob.type === 'application/json') {
          const text = await blob.text();
          const errorData = JSON.parse(text);
          message.error(errorData.error || 'Failed to generate report');
          return;
        }

        if (blob && blob.size > 0) {
          const tabCount = (reportTabsModels ?? []).length;
          const csvIsZipArchive =
            format === 'csv' &&
            (blob.type === 'application/zip' ||
              blob.type === 'application/x-zip-compressed' ||
              tabCount > 1);

          const ext =
            format === 'csv' ? (csvIsZipArchive ? '.zip' : '.csv') : '.xlsx';
          downloadReport(blob, `${safeTitle}${ext}`);
          message.success(
            format === 'csv'
              ? csvIsZipArchive
                ? 'ZIP archive downloaded (contains one CSV per tab)'
                : 'CSV report downloaded successfully'
              : 'Report downloaded successfully'
          );
        } else {
          message.error('No data received or file is empty');
        }
      })
      .catch(async (error) => {
        if (error?.data instanceof Blob) {
          try {
            const text = await error.data.text();
            const errorData = JSON.parse(text);
            message.error(errorData.error || 'Failed to download report');
          } catch {
            message.error('Failed to download report');
          }
        } else {
          message.error('Failed to download report');
        }
        unauthorize(error);
      })
      .finally(() => {
        hideSpin();
      });
  };

  const handleRefreshComplete = useCallback(() => {
    const { filterArray, appliedFilters, field_timezones, tabIndex } =
      reportFilterStateRef.current;
    fetchShipmentReportData(
      1,
      true,
      filterArray,
      appliedFilters,
      true,
      false,
      tabIndex,
      field_timezones
    );
  }, [fetchShipmentReportData]);

  const getRecalculateBody = useCallback(() => {
    const tab = multipleTabs[activeTabIndex];
    const payload =
      buildPayload(tab?.filters?.filterArray, tab?.filters?.appliedFilters) ||
      null;

    return {
      ...(payload ?? {}),
      ...(tab?.filters?.field_timezones &&
      Object.keys(tab?.filters?.field_timezones).length > 0
        ? {
            field_timezones: tab.filters.field_timezones,
          }
        : {}),
    };
  }, [activeTabIndex, multipleTabs, buildPayload]);

  //on scroll load more data
  const handleLoadMore = useCallback(() => {
    if (loadingMoreRef.current) return; // prevent double call
    loadingMoreRef.current = true;
    isAddingTabRef.current = false;
    setLoading(true);
    setMultipleTabs((prevTabs) => {
      return prevTabs.map((tab, index) => {
        if (index === activeTabIndex) {
          const tabIndex = activeTabIndex;
          const nextPage = (tab.page ?? 1) + 1;
          fetchShipmentReportData(
            nextPage,
            false,
            tab.filters?.filterArray ?? [],
            tab.filters?.appliedFilters ?? [],
            false,
            true,
            tabIndex,
            tab.filters?.field_timezones
          );
          return { ...tab, page: nextPage };
        }
        return tab;
      });
    });
  }, [activeTabIndex, fetchShipmentReportData]);

  const modalStyle: React.CSSProperties = {
    top: '1vh',
    height: '98vh',
    maxWidth: 'none',
    margin: '0 auto',
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    width: 'calc(100vw - 32px)',
  };

  const bodyStyle: React.CSSProperties = {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    height: '100%',
    margin: 0,
  };

  const [isSearchVisible, setIsSearchVisible] = useState(false);
  const [searchMatches, setSearchMatches] = useState<HTMLDivElement[]>([]);
  const [activeMatchIndex, setActiveMatchIndex] = useState(-1);

  const onSearchChange = (val: string) => {
    setSearchQuery(val);
    setActiveMatchIndex(-1);
    setCanUpdateHeaders(false);
    setMultipleTabs((prev) =>
      prev.map((tab) => ({
        ...tab,
        page: 1,
      }))
    );
  };

  useEffect(() => {
    if (searchMatches?.length > 0) {
      setActiveMatchIndex(0);
    }
  }, [shipmentData, searchMatches?.length]);

  const handleCloseSearch = () => {
    setIsSearchVisible(false);
    setSearchQuery('');
    setActiveMatchIndex(-1);
    setSearchMatches([]);
    setCanUpdateHeaders(false);
    TableScrollToTop();
    setMultipleTabs((prev) =>
      prev.map((tab) => ({
        ...tab,
        page: 1,
      }))
    );
  };

  const handleSearchIconClick = () => {
    setIsSearchVisible(true);
  };

  const next = () => {
    setActiveMatchIndex((prev) =>
      prev + 1 < searchMatches.length ? prev + 1 : 0
    );
  };

  const prev = () => {
    setActiveMatchIndex((prev) =>
      prev - 1 >= 0 ? prev - 1 : searchMatches.length - 1
    );
  };

  const handleFoundMatches = useCallback((matches: HTMLDivElement[]) => {
    setSearchMatches(matches);
    setActiveMatchIndex(matches && matches.length ? 0 : -1);
  }, []);

  const [isEditing, setIsEditing] = useState(false);
  const handleSave = () => setIsEditing(false);

  return (
    <Modal
      title=''
      open={reportModalOpen}
      width={'calc(100vw - 32px)'}
      closeIcon={null}
      footer={null}
      style={modalStyle}
      bodyStyle={bodyStyle}
      styles={{ body: bodyStyle }}
      className='report-modal-ant-wrapper'
    >
      <div className='zl-flex-between-column'>
        <Flex vertical gap={12}>
          <DownloadReportModalHeader
            setReportModalOpen={setReportModalOpen}
            onBack={onBack}
            title={title}
            showBackIcon={showBackIcon}
            presetAnchorRef={presetHeaderAnchorRef}
          />

          <Divider
            className='notification-divider'
            style={{ marginTop: '0px' }}
          />
        </Flex>
        <Row wrap={false} style={{ flex: 1, minHeight: 0 }}>
          <Col
            span={17}
            style={{
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              minWidth: 0,
              overflow: 'hidden',
            }}
          >
            <Flex
              className='py-4'
              align='center'
              justify='space-between'
              style={{ flexShrink: 0 }}
            >
              <Flex gap={10} align='center' className='report-header-buttons'>
                <Flex gap={8} align='center'>
                  {!isEditing ? (
                    <>
                      <p className='download-report-sheet-title'>
                        {sheetTitle}
                      </p>
                      <div
                        className='height-24 cursor-pointer'
                        onClick={() => setIsEditing(true)}
                      >
                        <PencilIcon
                          width={20}
                          height={20}
                          color='var(--text-primary)'
                        />
                      </div>
                    </>
                  ) : (
                    <div className='download-report-editor-input'>
                      <Input
                        size='small'
                        value={sheetTitle}
                        autoFocus
                        onChange={(e) => setSheetTitle(e.target.value)}
                        onPressEnter={handleSave}
                        onBlur={handleSave}
                        style={{ width: 240, border: '0', height: 36 }}
                      />
                      <CloseFilledIcon
                        onClick={() => setIsEditing(false)}
                        className='close-input-icon'
                      />
                    </div>
                  )}
                </Flex>
                {/* spin  button here */}
                <ReportRefreshButton
                  ref={refreshButtonRef}
                  reportModalOpen={reportModalOpen}
                  lastRefreshTime={lastRefreshTime}
                  onRefreshComplete={handleRefreshComplete}
                  getRecalculateBody={getRecalculateBody}
                />
              </Flex>

              <CustomButton
                text={'Add empty column'}
                bgColor='var(--gray-300)'
                color='var(--text-primary)'
                fontSize='12px'
                fontWeight='500'
                height={'28px'}
                borderRadius='20px'
                padding='6px 12px'
                boxShadow='none'
                iconPosition='left'
                icon={<PlusOutlined style={{ marginRight: '8px' }} />}
                onClick={() => {
                  setScrollOnNextAdd(true);
                  handleAddCustomColumn();
                }}
              />
            </Flex>

            <Flex
              align='center'
              gap={8}
              style={{ width: '100%', flexShrink: 0 }}
            >
              {/* genral search here   */}
              <div className='report-toolbar-search'>
                {isSearchVisible && (
                  <div className='reprt-search-wrapper'>
                    <SearchWithMatchNavigator
                      searchQuery={searchQuery}
                      setSearchQuery={onSearchChange}
                      activeView={searchMatches.length ? true : false}
                      totalMatches={searchMatches.length}
                      currentMatchNumber={
                        searchMatches.length ? activeMatchIndex + 1 : 0
                      }
                      handlePrevMatch={prev}
                      handleNextMatch={next}
                      formatPaste={true}
                    />
                  </div>
                )}
                {!isSearchVisible ? (
                  <Flex
                    align='center'
                    justify='center'
                    className='report-search-trigger'
                    onClick={handleSearchIconClick}
                  >
                    <SearchOutlined className='report-search-icon' />
                  </Flex>
                ) : (
                  <button
                    onClick={handleCloseSearch}
                    className='zl-btn icon-btn-28 radius-full'
                  >
                    <CrossIcon />
                  </button>
                )}
              </div>

              <Divider type='vertical' className='m-0 h-full' />
              <div className='report-filter-toolbar'>
                <ShipmentsFilterPanel
                  savedFilterKey='report'
                  token={token ?? ''}
                  appliedFilters={
                    multipleTabs[activeTabIndex]?.filters?.appliedFilters ?? []
                  }
                  setAppliedFilters={setReportAppliedFilters}
                  filterArray={
                    multipleTabs[activeTabIndex]?.filters?.filterArray ?? []
                  }
                  setFilterArray={setReportFilterArray}
                  setFiltersPayload={() => {}}
                  searchQuery={reportPanelSearchQuery}
                  setSearchQuery={setReportPanelSearchQuery}
                  isSearchVisible={reportPanelSearchVisible}
                  setIsSearchVisible={setReportPanelSearchVisible}
                  page={multipleTabs[activeTabIndex]?.page ?? 1}
                  pageSize={REPORT_PAGE_SIZE}
                  onRunSearch={handleReportFilterRunSearch}
                  showSearch={false}
                  persistState={false}
                  modalMode
                  dropdownMaxHeight='calc(100vh - 250px)'
                />
              </div>
            </Flex>

            <div className='report-table-shell'>
              <DownloadReportTableSection
                hasMore={multipleTabs[activeTabIndex]?.hasMore}
                columnTimeSettings={
                  multipleTabs[activeTabIndex]?.filters?.field_timezones ?? {}
                }
                onTimeSettingsChange={onTimeSettingsChange}
                headerList={multipleTabs[activeTabIndex]?.headers || []}
                shipmentData={
                  multipleTabs[activeTabIndex]?.data ?? EMPTY_SHIPMENT_DATA
                }
                onDeleteCustomColumn={handleDeleteCustomColumn}
                onCustomCellChange={handleCustomCellChange}
                onHeaderUpdate={handleHeaderUpdate}
                handleLoadMore={handleLoadMore}
                loading={loading}
                searchQuery={searchQuery}
                onFoundMatches={handleFoundMatches}
                activeMatchIndex={activeMatchIndex}
                scrollOnNextAdd={scrollOnNextAdd}
                setScrollOnNextAdd={setScrollOnNextAdd}
              />
            </div>

            <div className='report-tabs-shell'>
              <DownloadReportTabs
                tabs={multipleTabs}
                activeTabIndex={activeTabIndex}
                setActiveTabIndex={setActiveTabIndex}
                setMultipleTabs={setMultipleTabs}
                handleTabChange={handleTabChange}
                setCanUpdateHeaders={setCanUpdateHeaders}
                isTabSwitchRef={isTabSwitchRef}
                isAddingTabRef={isAddingTabRef}
              />
            </div>
          </Col>

          <Col span={1} style={{ display: 'flex', justifyContent: 'center' }}>
            <Divider type='vertical' className='notification-divider h-full' />
          </Col>
          <Col
            span={6}
            style={{
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              height: '100%',
            }}
          >
            <DownloadReportPannelSection
              appliedHeaderList={multipleTabs[activeTabIndex]?.headers ?? []}
              sendHeadersToParent={handleupdateHeaders}
              onPresetApplied={handlePresetApplied}
              appliedFilters={
                multipleTabs[activeTabIndex]?.filters?.appliedFilters ?? []
              }
              filterArray={
                multipleTabs[activeTabIndex]?.filters?.filterArray ?? []
              }
              sheetTitle={sheetTitle}
              headerList={multipleTabs[activeTabIndex]?.headers || []}
              tabs={multipleTabs}
              getTabsForSave={getTabsForSave}
              isReportLoading={loading}
              permissions={permissions}
              presetPortalHost={presetHeaderAnchorEl}
            />
          </Col>
        </Row>
        <div>
          <Divider className='notification-divider m-0' />
          <DownloadReportFooter
            schedules={schedules}
            onCreateSchedule={handleCreateSchedule}
            onEditSchedule={handleEditSchedule}
            setReportModalOpen={setReportModalOpen}
            handleDownload={handleDownload}
            totalRows={multipleTabs[activeTabIndex]?.total_rows}
            loading={loading}
          />
          <ScheduleDownloadModal
            onDelete={handleDeleteSchedule}
            open={scheduleModalOpen}
            mode={editingSchedule ? 'edit' : 'new'}
            initialValues={editingSchedule}
            onCancel={() => setScheduleModalOpen(false)}
            onSubmit={handleScheduleSubmit}
            recipientData={recipient}
            loggedInUserId={loggedInUserId ?? ''}
          />
        </div>
      </div>
    </Modal>
  );
};

export default DownloadReportModal;
